import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LayoutEditorContextType } from '../../types/context';
import {
  JSONObject,
  Layout,
  LayoutCanvas,
  LayoutDefinition,
  LayoutInlineForm,
  LayoutObject,
} from '../../types/types';
import { useLayoutDefinition } from '../../api/hooks/useLayoutDefinition';
import { useLayout } from '../../api/hooks/useLayout';
import { useSaveLayout } from '../../api/hooks/useSaveLayout';
import { PutLayoutResponse } from '../../api/requests/layout/putLayout';
import { HandlerRequestContext } from '../../api/types/types';
import { useRequestDataContext } from '../../hooks/context/useRequestDataContext';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useLayoutObjectTemplateContext } from '../../hooks/context/useLayoutObjectTemplateContext';
import { resolveTemplate } from '../../utils/json/resolveTemplate';
import { sanitizeCanvasPatch } from '../../utils/layouts/sanitizeCanvasPatch';
import { validateLayoutContent } from '../../utils/layouts/validateLayoutContent';
import { useLayoutFullscreenReference } from './hooks/useLayoutFullscreenReference';
import { useLayoutMatrix } from './hooks/useLayoutMatrix';
import { useLayoutObjects } from './hooks/useLayoutObjects';
import { useLayoutSaveErrors } from './hooks/useLayoutSaveErrors';
import { createLayoutPutBody } from './utils/createLayoutPutBody';
import { seedLayoutContent } from './utils/seedLayoutContent';
import { defaultValues } from './defaultValues';
import { LAYOUT_EDITOR_EMPTY_CANVAS } from '../../constants';

// Snapshot of the last loaded layout content; reference for isDirty and reset.
// Live objects pair with their snapshot counterparts by the server-assigned id,
// so index shifts (deletion) don't mark the survivors as changed.
type LayoutBaseline = {
  canvas: LayoutCanvas;
  objects: LayoutObject[];
};

export const LayoutEditorContext =
  createContext<LayoutEditorContextType>(defaultValues);

interface LayoutEditorProviderProps {
  layoutId: string;
  objectId?: string;
  children: ReactNode;
}

// Owns the layout editor data flow (definition fetch → apiMethods.get resolve → layout fetch)
// and the canonical editor state (canvas + objects + selection) shared by the Konva
// canvas and the settings panels. Must sit inside a RequestDataProvider.
const LayoutEditorProvider = ({
  layoutId,
  objectId,
  children,
}: LayoutEditorProviderProps) => {
  const [canvas, setCanvas] = useState<LayoutCanvas>(
    LAYOUT_EDITOR_EMPTY_CANVAS
  );
  const [baseline, setBaseline] = useState<LayoutBaseline | null>(null);
  const [focusedObjectField, setFocusedObjectField] = useState<string | null>(
    null
  );

  // Pending save-validation errors of the canvas/object fields.
  const {
    saveErrors,
    saveErrorsVersion,
    setSaveErrors,
    reportSaveErrors,
    clearObjectFieldErrors,
    clearCanvasFieldErrors,
  } = useLayoutSaveErrors();

  const requestData = useRequestDataContext();
  const { t } = useTranslate();
  // Last selected object — new objects inherit its colors, font and size.
  const { template, rememberObject } = useLayoutObjectTemplateContext();
  const {
    data: definition,
    error: definitionError,
    isLoading: isDefinitionLoading,
    fetch: fetchDefinition,
  } = useLayoutDefinition(layoutId);
  const {
    data: layout,
    error: layoutError,
    isLoading: isLayoutLoading,
    fetch: fetchLayout,
    detailedFetch: detailedFetchLayout,
  } = useLayout();
  const { isSaving, save, detailedSave } = useSaveLayout();

  // Arrangement mode is fixed per layout type — it comes from the definition,
  // never from the layout data.
  const isFixedMode = definition?.mode === 'fixed';

  // Template scope of the layout apiMethods (put/matrixDefault/matrixGenerate):
  // layout request data + the raw canvas field values under `canvasForm`
  // (no request-body sanitization — the expressions read the fields as-is).
  const getApiResolveData = (): HandlerRequestContext =>
    ({
      ...((requestData ?? {}) as unknown as JSONObject),
      canvasForm: canvas,
    }) as HandlerRequestContext;

  // Object list + selection + every mutation of them (add/paste/move/remove,
  // field edits, the copy mark).
  const {
    objects,
    setObjects,
    selectedIndex,
    setSelectedIndex,
    copiedObject,
    selectObject,
    updateObject,
    addObject,
    removeObject,
    moveObject,
    copySelectedObject,
    pasteObject,
    isObjectCopied,
    clearCopiedObjectMark,
  } = useLayoutObjects({
    canvas,
    isFixedMode,
    objectForm: definition?.objectForm,
    template,
    clearObjectFieldErrors,
  });

  // "Generate matrix" dialog + its api methods; the generated objects land in
  // the object list above.
  const {
    isMatrixSupported,
    isMatrixDialogOpen,
    matrixFormData,
    openMatrixDialog,
    closeMatrixDialog,
    seedMatrixFormData,
    patchMatrixFormData,
    fetchMatrixDefaults,
    generateMatrix,
  } = useLayoutMatrix({
    definition,
    canvas,
    isFixedMode,
    template,
    objects,
    setObjects,
    setSelectedIndex,
    getApiResolveData,
  });

  // Fullscreen reference picker — the picked value goes back to the field that
  // opened it (canvas / object / matrix dialog).
  const {
    fullscreenReference,
    openFullscreenReference,
    closeFullscreenReference,
    submitFullscreenReference,
  } = useLayoutFullscreenReference({
    definition,
    selectedIndex,
    // Wrapped: patchCanvas is declared below this call.
    patchCanvas: (patch: Partial<LayoutCanvas>) => patchCanvas(patch),
    updateObject,
    patchMatrixFormData,
  });

  const isLoading = isDefinitionLoading || isLayoutLoading;
  const error = definitionError ?? layoutError;

  const isDirty = useMemo(() => {
    if (!baseline) {
      return false;
    }

    // Fixed layouts don't persist the canvas (columns is a live editor
    // preference) — only the object list counts as a change.
    if (isFixedMode) {
      return JSON.stringify(objects) !== JSON.stringify(baseline.objects);
    }

    return (
      JSON.stringify(canvas) !== JSON.stringify(baseline.canvas) ||
      JSON.stringify(objects) !== JSON.stringify(baseline.objects)
    );
  }, [canvas, objects, baseline, isFixedMode]);

  // Seeds the editor from a freshly loaded layout and stores it as the isDirty/reset baseline.
  const initLayout = (
    nextLayout: Layout,
    objectForm?: LayoutInlineForm,
    canvasForm?: LayoutInlineForm
  ): void => {
    const seeded = seedLayoutContent(nextLayout, objectForm, canvasForm);

    setCanvas(seeded.canvas);
    setObjects(seeded.objects);
    setSelectedIndex(null);
    setBaseline(seeded);
    setSaveErrors([]);
  };

  // Baseline counterpart of the object at the given live index, paired by the
  // server-assigned id; undefined = no counterpart (an object created in the
  // editor — it has no id until the layout is saved and reloaded).
  const getBaselineObject = (index: number): LayoutObject | undefined => {
    if (!baseline) {
      return undefined;
    }

    // Fixed objects carry no id — the position IS the identity (the PUT is a
    // full replace of the ordered list).
    if (isFixedMode) {
      return baseline.objects[index];
    }

    const id = objects[index]?.id;

    if (id === undefined) {
      return undefined;
    }

    return baseline.objects.find((object) => object.id === id);
  };

  // Merges the patch into the canvas settings; cleared values fall back to defaults
  // (or keep the current value for keys without one) so no canvas key ever goes empty.
  const patchCanvas = (patch: Partial<LayoutCanvas>): void => {
    clearCanvasFieldErrors(patch);
    setCanvas((current) => {
      const next = { ...current, ...sanitizeCanvasPatch(patch, current) };
      return next;
    });
  };

  // Programmatic form default (e.g. radioBtn first option for an undefined value):
  // merged into the object AND its baseline counterpart, so it never counts as a change.
  const applyObjectDefault = (
    index: number,
    patch: Partial<LayoutObject>
  ): void => {
    const objectId = objects[index]?.id;

    setObjects((current) =>
      current.map((object, i) =>
        i === index ? { ...object, ...patch } : object
      )
    );
    setBaseline((current) => {
      const baselineIndex =
        current && objectId !== undefined
          ? current.objects.findIndex((object) => object.id === objectId)
          : -1;

      // A new object has no baseline counterpart — it is dirty as a whole anyway.
      if (!current || baselineIndex === -1) {
        return current;
      }

      return {
        ...current,
        objects: current.objects.map((object, i) =>
          i === baselineIndex ? { ...object, ...patch } : object
        ),
      };
    });
  };

  // Canvas variant of applyObjectDefault: state + baseline, never counts as a change.
  // Sanitized like patchCanvas — a programmatic '' (input normalization) must not
  // land in the baseline and leak into the PUT body.
  const applyCanvasDefault = (patch: Partial<LayoutCanvas>): void => {
    const sanitized = sanitizeCanvasPatch(patch, canvas);

    setCanvas((current) => ({ ...current, ...sanitized }));
    setBaseline((current) =>
      current
        ? { ...current, canvas: { ...current.canvas, ...sanitized } }
        : current
    );
  };

  // Saves the whole layout content via the definition's apiMethods.put; on success
  // the current state becomes the new isDirty/reset baseline. Errors (incl. 449 force
  // retry) go through the shared api error layer inside useSaveLayout.
  const saveLayout = async (): Promise<boolean> => {
    if (!definition) {
      return false;
    }

    // Client-side validation gate: an invalid layout never reaches the API —
    // mandatory items must be filled, filled values must pass the field rules.
    // Errors render under the offending inputs (SettingsPanelItem via saveErrors).
    // Fixed layouts don't save the canvas, so it is not validated there.
    const validationErrors = validateLayoutContent(
      isFixedMode ? undefined : canvas,
      objects,
      definition.canvasForm,
      definition.objectForm
    );

    if (validationErrors.length > 0) {
      reportSaveErrors(validationErrors);

      // Bring the first invalid object into view — its form shows the errors.
      const firstObjectError = validationErrors.find(
        (validationError) => validationError.scope === 'object'
      );
      if (firstObjectError?.index !== undefined) {
        setSelectedIndex(firstObjectError.index);
      }

      return false;
    }

    setSaveErrors([]);

    const body = createLayoutPutBody({
      isFixedMode,
      layout,
      canvas,
      objects,
      definition,
    });
    const put = definition.apiMethods.put;
    const resolveData = getApiResolveData();

    let response: PutLayoutResponse | undefined;

    if (typeof put === 'string') {
      const { url } = await resolveTemplate({ url: put }, resolveData);
      response = await save(url, body);
    } else {
      response = await detailedSave(put, resolveData, body);
    }

    if (!response?.success) {
      return false;
    }

    toast(t('toasts.layoutSaved'));

    // Reload from the server instead of snapshotting the local state: the
    // save assigns ids to the new objects and the id-based dirty/new pairing
    // needs them (plus any server-side normalization comes along).
    await loadLayout(definition);
    return true;
  };

  // Field of the canvas settings differs from the baseline value.
  const isCanvasFieldDirty = (name: string): boolean =>
    !!baseline &&
    JSON.stringify(canvas[name]) !== JSON.stringify(baseline.canvas[name]);

  // Field of the object at the given index differs from its baseline counterpart
  // (a new object without a counterpart is dirty in every field).
  const isObjectFieldDirty = (index: number, name: string): boolean => {
    if (!baseline) {
      return false;
    }

    const baselineObject = getBaselineObject(index);

    if (!baselineObject) {
      return true;
    }

    return (
      JSON.stringify(objects[index]?.[name]) !==
      JSON.stringify(baselineObject[name])
    );
  };

  // Object at the given index differs from its baseline counterpart.
  const isObjectDirty = (index: number): boolean =>
    !!baseline &&
    JSON.stringify(objects[index]) !== JSON.stringify(getBaselineObject(index));

  // Object has no baseline counterpart (added after the last load/save).
  const isObjectNew = (index: number): boolean =>
    !!baseline && getBaselineObject(index) === undefined;

  // Discards local changes and restores the last loaded/saved layout content.
  const reset = (): void => {
    if (!baseline) {
      return;
    }

    setCanvas(baseline.canvas);
    setObjects(baseline.objects);
    setSelectedIndex(null);
    setSaveErrors([]);
  };

  // Loads the layout content via the definition's apiMethods.get and seeds the
  // editor state (incl. the isDirty/reset baseline).
  const loadLayout = async (
    targetDefinition: LayoutDefinition
  ): Promise<void> => {
    const get = targetDefinition.apiMethods.get;
    const resolveData = (requestData ?? {}) as unknown as HandlerRequestContext;

    if (typeof get === 'string') {
      const { url } = await resolveTemplate({ url: get }, resolveData);
      const loadedLayout = await fetchLayout(url);
      if (loadedLayout) {
        initLayout(
          loadedLayout,
          targetDefinition.objectForm,
          targetDefinition.canvasForm
        );
      }
      return;
    }

    const loadedLayout = await detailedFetchLayout(get, resolveData);
    if (loadedLayout) {
      initLayout(
        loadedLayout,
        targetDefinition.objectForm,
        targetDefinition.canvasForm
      );
    }
  };

  // Keeps the template in sync with the selected object (including its live
  // edits); deselection leaves the last remembered one in place.
  useEffect(() => {
    if (selectedIndex !== null && objects[selectedIndex]) {
      rememberObject(objects[selectedIndex]);
    }
  }, [selectedIndex, objects]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      //1) Definition load
      const loadedDefinition = await fetchDefinition();

      if (!loadedDefinition || !objectId) {
        return;
      }

      //2) Layout load (+ seed the editor state)
      await loadLayout(loadedDefinition);
    };

    load();
  }, [layoutId, objectId, requestData]);

  const value: LayoutEditorContextType = useMemo(
    () => ({
      isProvided: true,
      definition,
      layout,
      isLoading,
      error,
      isSaving,
      canvas,
      objects,
      selectedIndex,
      isDirty,
      isFixedMode,
      selectObject,
      updateObject,
      addObject,
      removeObject,
      moveObject,
      copySelectedObject,
      pasteObject,
      isObjectCopied,
      clearCopiedObjectMark,
      patchCanvas,
      applyObjectDefault,
      applyCanvasDefault,
      saveErrors,
      saveErrorsVersion,
      saveLayout,
      reset,
      isMatrixSupported,
      fetchMatrixDefaults,
      generateMatrix,
      isMatrixDialogOpen,
      matrixFormData,
      openMatrixDialog,
      closeMatrixDialog,
      seedMatrixFormData,
      patchMatrixFormData,
      fullscreenReference,
      openFullscreenReference,
      closeFullscreenReference,
      submitFullscreenReference,
      isCanvasFieldDirty,
      isObjectFieldDirty,
      isObjectDirty,
      isObjectNew,
      focusedObjectField,
      setFocusedObjectField,
    }),
    [
      definition,
      layout,
      isLoading,
      error,
      isSaving,
      canvas,
      objects,
      selectedIndex,
      isDirty,
      baseline,
      saveErrors,
      saveErrorsVersion,
      copiedObject,
      template,
      isMatrixDialogOpen,
      matrixFormData,
      fullscreenReference,
      focusedObjectField,
    ]
  );

  return (
    <LayoutEditorContext.Provider value={value}>
      {children}
    </LayoutEditorContext.Provider>
  );
};

export default LayoutEditorProvider;
